/**
 * Company and contact rules: an email, a phone, a company name, the IBAN
 * payouts go to, the Bulgarian company id (EIK / BULSTAT) and a VAT number,
 * and the company details an invoice is made out to. The address rules they
 * build on are in address-validation.js. The API checks the same rules
 * (aldoo-na/store/contact-validation.js) — keep the two in step.
 *
 * Every check answers with an error code, or null when the value is good; a
 * value that is not text (a number, an array, an object) fails as it is,
 * rather than being turned into "[object Object]" or a database error.
 */
import { MAX_LENGTH, isText, isRecord, collapse, personNameError, normalizePhone, validateAddress, normalizeAddress } from "./address-validation"

// RFC-lite: one @, a dot in the domain, a two-letter-or-longer ending, and
// none of the characters that make an address a list or a piece of markup
export const EMAIL = /^[^\s@<>()[\],;:"]+@[^\s@<>()[\],;:"]+\.[a-z]{2,}$/i
const PHONE_CHARS = /[^\d+\s\-()]/g
// markup has no business in a name
const MARKUP = /[<>]/
// control characters (a pasted tab, a zero-width joiner) are not typed on purpose
const CONTROL = /[\u0000-\u001f\u007f\u200b-\u200f\u2028\u2029\ufeff]/

/**
 * IBAN length by country (the SWIFT IBAN registry). A country not here has no
 * IBAN, so an IBAN that starts with it is not one.
 */
export const IBAN_LENGTH = {
  AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29, BY: 28, CH: 21, CR: 22, CY: 28, CZ: 24,
  DE: 22, DK: 18, DO: 28, EE: 20, EG: 29, ES: 24, FI: 18, FO: 18, FR: 27, GB: 22, GE: 22, GI: 23, GL: 18, GR: 27, GT: 28,
  HR: 21, HU: 28, IE: 22, IL: 23, IQ: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28, LC: 32, LI: 21, LT: 20, LU: 20,
  LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27, MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29,
  RO: 24, RS: 22, SA: 24, SC: 31, SE: 24, SI: 19, SK: 24, SM: 27, ST: 25, SV: 28, TL: 23, TN: 24, TR: 26, UA: 29, VA: 22,
  VG: 24, XK: 20,
}

/** An email address: "email_required" | "email_invalid" | null. */
export function emailError(value, { required = true } = {}) {
  if (!isText(value)) return "email_invalid"
  const email = String(value ?? "").trim()
  if (!email) return required ? "email_required" : null
  if (email.length > MAX_LENGTH.email || !EMAIL.test(email)) return "email_invalid"
  return null
}

export const normalizeEmail = (value) => String(value ?? "").trim()

/**
 * A phone number someone can be called on (the address rules' normalizePhone,
 * so +359 88 123 4567, 0881234567 and 00359881234567 are the same number):
 * "phone_required" | "phone_invalid" | null.
 */
export function phoneError(value, { required = true, country = "BG" } = {}) {
  if (!isText(value)) return "phone_invalid"
  const phone = collapse(value)
  if (!phone) return required ? "phone_required" : null
  if (phone.length > MAX_LENGTH.phone || phone !== phone.replace(PHONE_CHARS, "") || !normalizePhone(phone, country)) return "phone_invalid"
  return null
}

/** The phone as it is stored: international (+359881234567) when it can be. */
export const normalizeContactPhone = (value, country = "BG") => {
  const phone = collapse(value)
  return (phone && normalizePhone(phone, country)) || phone
}

/**
 * A company's name as it is registered or known: 2–120 characters, at least
 * one letter, no markup, no control characters.
 */
export function companyNameError(value, { required = true } = {}) {
  if (!isText(value)) return "company_invalid"
  const name = collapse(value)
  if (!name) return required ? "company_required" : null
  if (name.length < 2 || name.length > MAX_LENGTH.company || !/\p{L}/u.test(name) || MARKUP.test(name) || CONTROL.test(name)) return "company_invalid"
  return null
}

export const normalizeIban = (value) => String(value ?? "").replace(/\s+/g, "").toUpperCase()

/** The IBAN mod-97 check: the country and check digits moved to the end, letters as numbers. */
function ibanChecksumOk(iban) {
  const digits = (iban.slice(4) + iban.slice(0, 4)).replace(/[A-Z]/g, (letter) => String(letter.charCodeAt(0) - 55))
  let rest = 0
  for (const digit of digits) rest = (rest * 10 + Number(digit)) % 97
  return rest === 1
}

/** An IBAN: its country's length and the mod-97 check. Spaces do not count. */
export function ibanError(value, { required = false } = {}) {
  if (!isText(value)) return "iban_invalid"
  const iban = normalizeIban(value)
  if (!iban) return required ? "iban_required" : null
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban) || IBAN_LENGTH[iban.slice(0, 2)] !== iban.length || !ibanChecksumOk(iban)) return "iban_invalid"
  return null
}

const weighted = (digits, weights) => weights.reduce((sum, weight, i) => sum + weight * Number(digits[i]), 0)

/**
 * A Bulgarian company id (ЕИК / БУЛСТАТ): 9 digits, or 13 for a branch, each
 * with its check digit — the ninth over the first eight (weights 1–8, then
 * 3–10 when that leaves 10), the thirteenth over digits 9–12 (weights 2 7 3 5,
 * then 4 9 5 7).
 */
export function eikValid(value) {
  const eik = String(value ?? "")
  if (!/^(\d{9}|\d{13})$/.test(eik) || /^0+$/.test(eik)) return false
  let check = weighted(eik, [1, 2, 3, 4, 5, 6, 7, 8]) % 11
  if (check === 10) check = weighted(eik, [3, 4, 5, 6, 7, 8, 9, 10]) % 11 % 10
  if (check !== Number(eik[8])) return false
  if (eik.length === 9) return true
  const branch = eik.slice(8)
  check = weighted(branch, [2, 7, 3, 5]) % 11
  if (check === 10) check = weighted(branch, [4, 9, 5, 7]) % 11 % 10
  return check === Number(eik[12])
}

/** A Bulgarian personal number (ЕГН), which is the VAT number of a sole trader. */
export function egnValid(value) {
  const egn = String(value ?? "")
  if (!/^\d{10}$/.test(egn)) return false
  return (weighted(egn, [2, 4, 8, 5, 10, 9, 7, 3, 6]) % 11) % 10 === Number(egn[9])
}

export const normalizeTaxNumber = (value) => String(value ?? "").replace(/[\s.\-]/g, "").toUpperCase()

/** A company id: an EIK / BULSTAT in Bulgaria, a registration number elsewhere. */
export function companyIdError(value, { country = "BG", required = true } = {}) {
  if (!isText(value)) return "company_id_invalid"
  const id = normalizeTaxNumber(value)
  if (!id) return required ? "company_id_required" : null
  if (country === "BG") return eikValid(id) ? null : "company_id_invalid"
  return /^[A-Z0-9][A-Z0-9\/]{1,19}$/.test(id) ? null : "company_id_invalid"
}

/**
 * A VAT number as stored: spaces and dots out, upper case — and in Bulgaria the
 * EIK (or ЕГН) alone, as it is often written, given its "BG".
 */
export const normalizeVat = (value, { country = "BG" } = {}) => {
  const vat = normalizeTaxNumber(value)
  return country === "BG" && /^(\d{9}|\d{10})$/.test(vat) ? `BG${vat}` : vat
}

/**
 * A VAT number: BG and the EIK (or a sole trader's ЕГН) in Bulgaria — the "BG"
 * may be left out — the country code and 2–12 letters or digits elsewhere.
 */
export function vatError(value, { required = false, country = "BG" } = {}) {
  if (!isText(value)) return "vat_invalid"
  const vat = normalizeVat(value, { country })
  if (!vat) return required ? "vat_required" : null
  if (vat.startsWith("BG")) {
    const digits = vat.slice(2)
    return (digits.length === 9 && eikValid(digits)) || egnValid(digits) ? null : "vat_invalid"
  }
  return /^[A-Z]{2}[0-9A-Z]{2,12}$/.test(vat) ? null : "vat_invalid"
}

// the company's address: everything an address has but who receives it
const INVOICE_ADDRESS_FIELDS = ["line1", "line2", "city", "postal_code", "country"]

/**
 * The invoice details as one shape, whichever the client sent: the stored
 * one `{ companyName, companyId, vatNumber, contactPerson, address: { line1,
 * city, postal_code, country } }`, the checkout's older flat one (`address`
 * the street, `postalCode`), or the framework's `taxId`.
 */
function invoiceInput(details) {
  const d = isRecord(details) ? details : {}
  const address = isRecord(d.address) ? d.address : { line1: d.address, city: d.city, postal_code: d.postal_code ?? d.postalCode, country: d.country }
  let { companyId, vatNumber } = d
  // the framework checkout sends one tax id: a VAT number when it starts with
  // a country code, the company id when it is only digits
  if (companyId === undefined && vatNumber === undefined && d.taxId !== undefined) {
    if (/^[A-Za-z]{2}/.test(String(d.taxId ?? ""))) vatNumber = d.taxId
    else companyId = d.taxId
  }
  return { companyName: d.companyName, companyId, vatNumber, contactPerson: d.contactPerson, address }
}

/**
 * { field: code } for the invoice details, and the details as they are
 * stored. Fields: companyName, companyId, vatNumber, contactPerson and the
 * address's line1, line2, city, postal_code, country. A Bulgarian company
 * gives its EIK / BULSTAT (the VAT number is optional); a company elsewhere
 * gives its company id or its VAT number. The contact person (МОЛ) is
 * optional, and a first and last name when given.
 */
export function validateInvoiceDetails(details) {
  const input = invoiceInput(details)
  const errors = {}
  const country = isText(input.address.country) ? collapse(input.address.country).toUpperCase() : ""

  const company = companyNameError(input.companyName)
  if (company) errors.companyName = company === "company_required" ? "company_name_required" : "company_name_invalid"

  const vat = vatError(input.vatNumber)
  if (vat) errors.vatNumber = vat
  const hasVat = !vat && !!normalizeTaxNumber(input.vatNumber)
  const companyId = companyIdError(input.companyId, { country, required: country === "BG" || !hasVat })
  if (companyId) errors.companyId = companyId === "company_id_required" && country !== "BG" ? "tax_id_required" : companyId

  if (input.contactPerson !== undefined && input.contactPerson !== null && input.contactPerson !== "") {
    if (personNameError(input.contactPerson, { full: true, required: false })) errors.contactPerson = "contact_person_invalid"
  }

  Object.assign(errors, validateAddress(input.address, { only: INVOICE_ADDRESS_FIELDS }))
  if (Object.keys(errors).length) return { errors, value: null }

  const address = normalizeAddress(input.address, INVOICE_ADDRESS_FIELDS)
  if (!address.line2) delete address.line2
  const value = {
    companyName: collapse(input.companyName),
    companyId: normalizeTaxNumber(input.companyId),
    vatNumber: normalizeVat(input.vatNumber),
    contactPerson: collapse(input.contactPerson),
    address,
  }
  // the one tax number the receipts and older readers look for
  value.taxId = value.vatNumber || value.companyId
  for (const key of ["companyId", "vatNumber", "contactPerson"]) if (!value[key]) delete value[key]
  return { errors, value }
}

/**
 * A courier setting a company fills in, by the format its provider gives it:
 * "digits" (an id the courier issued) or "positive_integer" (a service
 * number). Returns "setting_invalid" or null; empty is left to the caller.
 */
export function providerSettingError(format, value) {
  if (!format) return null
  if (typeof value !== "string" && typeof value !== "number") return "setting_invalid"
  const text = String(value).trim()
  if (!text) return null
  if (format === "digits") return /^\d{1,12}$/.test(text) ? null : "setting_invalid"
  if (format === "positive_integer") return /^[1-9]\d{0,8}$/.test(text) ? null : "setting_invalid"
  // an amount of money above zero, in the currency's units: "3", "3.2", "3,20"
  if (format === "money") return /^\d{1,4}([.,]\d{1,2})?$/.test(text) && Number(text.replace(",", ".")) > 0 ? null : "setting_invalid"
  return null
}
